'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';

export default function ModelPage() {
  const router = useRouter();
  const [model, setModel] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!model.trim()) return;
    router.push(`/connection?model=${encodeURIComponent(model.trim())}`);
  };

  return (
    <div style={{
      fontFamily: "'Roboto', sans-serif",
      color: '#122033',
      background: 'radial-gradient(circle at top left, #ffffff 0%, #f3f9ff 35%, #edf6ff 100%)',
      overflowX: 'hidden',
      minHeight: '100vh',
    }}>
      <SiteHeader />

      <main>
        {/* Hero Section */}
        <section className="py-6 sm:py-8 md:py-[34px]">
          <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6">
            <div style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '34px',
              border: '1px solid rgba(18,32,51,.06)',
              background: 'linear-gradient(135deg, rgba(255,255,255,.96), rgba(242,249,255,.92)), linear-gradient(120deg, #f7fbff, #ebf8ff)',
              boxShadow: '0 18px 50px rgba(16,46,80,.12)',
              maxWidth: '860px',
              margin: '0 auto',
            }}>
              <div className="px-4 py-8 sm:px-8 sm:py-10 md:px-[46px] md:py-[44px] text-center">
                <h1 style={{
                  margin: '0 0 14px',
                  fontSize: 'clamp(1.5rem, 4vw, 3.4rem)',
                  lineHeight: '1.15',
                  letterSpacing: '-1.2px',
                  fontWeight: 900,
                  maxWidth: '760px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  color: '#122033',
                  wordBreak: 'break-word',
                }}>
                  Let&apos;s Connect Your Printer To Your Wi&#8209;Fi or Device
                </h1>

                <p style={{
                  margin: '0 auto 26px',
                  maxWidth: '700px',
                  color: '#5c6b7d',
                  fontSize: '1.08rem',
                  lineHeight: '1.7',
                  fontWeight: 500,
                }}>
                  <strong style={{ fontWeight: 800, color: '#122033' }}>Enter your model number, product name or serial number:</strong>
                </p>

                {/* Form Card */}
                <form
                  onSubmit={handleSubmit}
                  className="max-w-[760px] mx-auto text-left rounded-3xl p-4 sm:p-5 md:p-[26px]"
                  style={{
                    background: 'rgba(255,255,255,.86)',
                    border: '1px solid rgba(18,32,51,.07)',
                    boxShadow: '0 16px 34px rgba(16,46,80,.08)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <input
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="Printer Model or Name"
                    required
                    autoComplete="off"
                    autoFocus
                    style={{
                      width: '100%',
                      height: '56px',
                      border: '1.5px solid rgba(18,32,51,.12)',
                      borderRadius: '18px',
                      background: '#fff',
                      padding: '0 18px',
                      fontSize: '1.02rem',
                      fontWeight: 700,
                      color: '#122033',
                      outline: 'none',
                      transition: '.2s ease',
                      boxShadow: '0 6px 16px rgba(16,46,80,.05)',
                      fontFamily: "'Roboto', sans-serif",
                    }}
                    className="field-focus"
                  />

                  <div className="mt-4 sm:mt-5 md:mt-[22px] flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-[14px] items-stretch sm:items-center">
                    <button
                      type="submit"
                      className="w-full sm:w-auto"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '56px',
                        padding: '0 30px',
                        border: 'none',
                        borderRadius: '18px',
                        background: 'linear-gradient(135deg, #0a8a8a, #0ca4a4)',
                        color: '#fff',
                        fontSize: '1.05rem',
                        fontWeight: 900,
                        letterSpacing: '.4px',
                        cursor: 'pointer',
                        boxShadow: '0 18px 35px rgba(10,138,138,.28)',
                        transition: '.25s ease',
                        fontFamily: "'Roboto', sans-serif",
                      }}
                    >
                      CONTINUE SETUP
                    </button>

                    <div style={{
                      fontSize: '.96rem',
                      color: '#4d657d',
                      fontWeight: 700,
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,.88)',
                      border: '1px solid rgba(18,32,51,.06)',
                      borderRadius: '16px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" width="18" height="18" style={{ flexShrink: 0 }}>
                        <path d="M12 3l7 4v5c0 4.4-2.9 8.5-7 9.7C7.9 20.5 5 16.4 5 12V7l7-4z" stroke="#0A8A8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.3 12.2l1.7 1.7 3.8-4.1" stroke="#0A8A8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>Enter your printer details to continue</span>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="pb-6 sm:pb-8 md:pb-[26px] pt-2 sm:pt-3 md:pt-[14px]">
          <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6">
            <div className="max-w-[860px] mx-auto rounded-[28px] p-5 sm:p-6 md:p-[28px_24px] text-center"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,.90), rgba(249,252,255,.98))',
                border: '1px solid rgba(18,32,51,.06)',
                boxShadow: '0 18px 38px rgba(16,46,80,.08)',
              }}>
              <h2 style={{
                fontSize: 'clamp(1.1rem, 2.4vw, 1.9rem)',
                fontWeight: 900,
                margin: '0 0 10px',
                letterSpacing: '-.4px',
                color: '#122033',
                lineHeight: '1.3',
              }}>
                Fill In The Printer Model And Continue The Setup
              </h2>
              <p style={{
                margin: '0 auto',
                maxWidth: '760px',
                color: '#5c6b7d',
                fontSize: '1rem',
                lineHeight: '1.7',
                fontWeight: 500,
              }}>
                Enter the printer details and continue to the next step to complete the guided setup process.
              </p>
              <div style={{ marginTop: '14px', color: '#ff7a00', fontSize: '1rem', fontWeight: 900 }}>
                You can enter product name, model number, or serial number.
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
